import { Injectable } from "@angular/core";
import { DocuSignCeremonyEvent } from "../domain/models/docusign-signing.model";

/** The subset of the DocuSign.js SDK the ceremony uses. */
export interface DocuSignSdk {
  signing(configuration: DocuSignSigningConfiguration): DocuSignSigningSession;
}

export interface DocuSignSigningConfiguration {
  /** The embedded signing URL minted by RYNO. */
  url: string;
  /** `focused` is the minimal, in-page ceremony; `default` is the classic full chrome. */
  displayFormat: "focused" | "default";
  style?: DocuSignSigningStyle;
}

export interface DocuSignSigningStyle {
  branding?: {
    primaryButton?: {
      backgroundColor?: string;
      color?: string;
    };
  };
  signingNavigationButton?: {
    finishText?: string;
    position?: "bottom-left" | "bottom-center" | "bottom-right";
  };
}

export interface DocuSignSigningSession {
  on(event: "ready", handler: () => void): void;
  on(event: "sessionEnd", handler: (payload: DocuSignCeremonyEvent | string) => void): void;
  mount(target: string | HTMLElement): void;
}

interface DocuSignGlobal {
  loadDocuSign(integrationKey: string): Promise<DocuSignSdk>;
}

/** Loads the DocuSign.js bundle and hands back an initialised SDK. */
@Injectable({
  providedIn: "root",
})
export class DocuSignSdkLoader {
  /** One in-flight load per (bundle, key) pair. */
  private readonly loads = new Map<string, Promise<DocuSignSdk>>();

  load(bundleUrl: string, integrationKey: string): Promise<DocuSignSdk> {
    const key = `${bundleUrl}|${integrationKey}`;
    const started = this.loads.get(key);

    if (started) {
      return started;
    }

    const load = this.injectScript(bundleUrl)
      .then(() => {
        const docuSign = (window as unknown as { DocuSign?: DocuSignGlobal }).DocuSign;

        if (!docuSign) {
          throw new Error("The DocuSign bundle loaded but exposed no DocuSign global.");
        }

        return docuSign.loadDocuSign(integrationKey);
      })
      .catch((error: unknown) => {
        this.loads.delete(key);

        throw error;
      });

    this.loads.set(key, load);

    return load;
  }

  private injectScript(bundleUrl: string): Promise<void> {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${bundleUrl}"]`);

    if (existing) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = bundleUrl;
      script.async = true;
      script.onload = (): void => resolve();
      script.onerror = (): void => {
        script.remove();
        reject(new Error(`Could not load the DocuSign bundle from ${bundleUrl}. Check script-src in the CSP.`));
      };

      document.body.appendChild(script);
    });
  }
}
